import React, { useState, useEffect, useContext } from "react";
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalCloseButton,
  ModalBody,
  Box,
  ModalHeader,
  Spinner,
} from "@chakra-ui/react";
import { appRequest } from "routes";
import FileViewer from "components/common/FileViewerPdfAndImage/FileViewer";
import { AppContext } from "AppProvider/AppContext";

type ModalProps = {
  isOpen: boolean;
  onClose: () => void;
  file: any;
  viewerWidth?: any;
  documentId?: any;
  localUrl?: string;
  localType?: string;
};

export default function FileViewerModal({
  isOpen,
  onClose,
  file,
  viewerWidth,
  documentId,
  localUrl,
  localType
}: ModalProps): JSX.Element {
  const { toastNotify } = useContext(AppContext);
  const [fetchFileLoader, setFetchFileLoader] = useState<boolean>(false);
  const [sample, setSample] = useState({
    url: "",
    file: null,
    type: null,
    buffer: null,
  });

  useEffect(() => {
    if (isOpen) {
      if (file?._id) {
        getFile();
      } else if (localUrl) {
        setSample({
          url: localUrl,
          file: file,
          type: localType || file?.type || "application/pdf",
          buffer: null,
        });
      }
    }
  }, [isOpen, file, localUrl]);

  const getFile = async () => {
    try {
      setFetchFileLoader(true);
      const response: any = await appRequest("filesystem", "get", {
        id: file._id,
      });
      if (response && !response.error) {
        let buffer =
          typeof response === "string" ? JSON.parse(response) : response;
        buffer = new Uint8Array(buffer);

        const fileURL = URL.createObjectURL(
          new Blob([buffer], { type: file?.metadata?.contentType || "application/pdf" })
        );
        setSample({
          url: fileURL,
          file: file,
          type: file?.metadata?.contentType || "application/pdf",
          buffer: buffer,
        });
      } else {
        // Handle error response
      }
    } catch (error: any) {
      toastNotify({
        title: "Error",
        message: error.message,
        type: "error",
      });
    } finally {
      setFetchFileLoader(false);
    }
  };

  const width = "80vw"; // Adjust width as needed

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="2xl" isCentered>
      <ModalOverlay bg="rgba(0, 0, 0, 0.2)" />
      <ModalContent
        position="fixed"
        right={isOpen ? "0" : `-${width}`}
        top="0"
        height="100vh"
        width={viewerWidth ? width : "100vw"}
        animation={`${isOpen ? "slideIn" : "slideOut"} 0.8s ease-in-out forwards`}
      >
        <ModalHeader fontSize={{ base: "md", lg: "xl" }} color="teal.300" fontWeight="700" p=".6rem .6rem" ml="1rem">
          Document
        </ModalHeader>
        <ModalCloseButton />
        <ModalBody px={{ base: ".5rem", lg: "1.5rem" }} py=".2rem" overflowY="auto" className="customScrollBar" mb={{ base: "1rem", lg: "unset" }}>
          <Box display="flex" justifyContent="center" alignItems="center" h="100%">
            {fetchFileLoader ? (
              <Spinner size="xl" thickness="4px" speed="0.65s" color="teal.400" />
            ) : (
              <FileViewer sample={sample} docId={documentId} />
            )}
          </Box>
        </ModalBody>
      </ModalContent>
      <style>
        {`
          @keyframes slideIn {
            from {
              right: -${width};
            }
            to {
              right: 0;
            }
          }
          @keyframes slideOut {
            from {
              right: 0;
            }
            to {
              right: -${width};
            }
          }
        `}
      </style>
    </Modal>
  );
}
